import type { AuthoredPitch } from './types.ts';
import type {
  AuthoredBank,
  AuthoredOrientation,
  AuthoredPieceDescriptor,
  AuthoredPieceCategory,
  AuthoredTrackType,
} from './types.ts';

const ALL_PITCHES = [-90, -60, -25, 0, 25, 60, 90] as const;
const LEVEL = [0] as const;
const ALL_BANKS = ['left', 'level', 'right'] as const;
const LEVEL_BANK = ['level'] as const;
const UPRIGHT = ['upright'] as const;

function straightSpan(length: number, rise = 0) {
  return [
    {
      end: { x: length, y: rise, z: 0 },
      control1: { x: Math.round(length / 3), y: Math.round(rise / 3), z: 0 },
      control2: { x: Math.round((length * 2) / 3), y: Math.round((rise * 2) / 3), z: 0 },
    },
  ] as const;
}

function tangentLinearSpan(endX: number, endY: number, tangentX: number, tangentY: number) {
  return [
    {
      end: { x: endX, y: endY, z: 0 },
      control1: { x: tangentX, y: tangentY, z: 0 },
      control2: { x: endX - tangentX, y: endY - tangentY, z: 0 },
    },
  ] as const;
}

function authoredChain(
  anchors: readonly { readonly x: number; readonly y: number; readonly z: number }[],
  options: {
    readonly windingDegrees?: readonly number[];
    readonly entryTangent?: { readonly x: number; readonly y: number; readonly z: number };
    readonly exitTangent?: { readonly x: number; readonly y: number; readonly z: number };
  } = {},
): AuthoredPieceDescriptor['spans'] {
  if (anchors.length < 2) throw new Error('An authored chain needs at least two anchors.');
  const tangentAt = (index: number) => {
    if (index === 0 && options.entryTangent) return options.entryTangent;
    if (index === anchors.length - 1 && options.exitTangent) return options.exitTangent;
    const before = anchors[Math.max(0, index - 1)]!;
    const after = anchors[Math.min(anchors.length - 1, index + 1)]!;
    return {
      x: Math.round((after.x - before.x) / 6),
      y: Math.round((after.y - before.y) / 6),
      z: Math.round((after.z - before.z) / 6),
    };
  };
  return anchors.slice(0, -1).map((start, index) => {
    const end = anchors[index + 1]!;
    const startTangent = tangentAt(index);
    const endTangent = tangentAt(index + 1);
    return {
      end,
      control1: {
        x: start.x + startTangent.x,
        y: start.y + startTangent.y,
        z: start.z + startTangent.z,
      },
      control2: {
        x: end.x - endTangent.x,
        y: end.y - endTangent.y,
        z: end.z - endTangent.z,
      },
      ...(options.windingDegrees
        ? {
            windingStartDegrees: options.windingDegrees[index]!,
            windingEndDegrees: options.windingDegrees[index + 1]!,
          }
        : {}),
    };
  });
}

function authoredParametricChain(
  segments: number,
  pointAt: (progress: number) => { x: number; y: number; z: number },
  derivativeAt: (progress: number) => { x: number; y: number; z: number },
  windingDegrees: number | ((progress: number) => number) = 0,
): AuthoredPieceDescriptor['spans'] {
  const controlScale = 1 / (segments * 3);
  const windingAt =
    typeof windingDegrees === 'function'
      ? windingDegrees
      : (progress: number) => windingDegrees * progress;
  const fixedPoint = (point: { x: number; y: number; z: number }) => ({
    x: Math.round(point.x),
    y: Math.round(point.y),
    z: Math.round(point.z),
  });
  return Array.from({ length: segments }, (_, index) => {
    const startProgress = index / segments;
    const endProgress = (index + 1) / segments;
    const start = fixedPoint(pointAt(startProgress));
    const end = fixedPoint(pointAt(endProgress));
    const startTangent = fixedPoint({
      x: derivativeAt(startProgress).x * controlScale,
      y: derivativeAt(startProgress).y * controlScale,
      z: derivativeAt(startProgress).z * controlScale,
    });
    const endTangent = fixedPoint({
      x: derivativeAt(endProgress).x * controlScale,
      y: derivativeAt(endProgress).y * controlScale,
      z: derivativeAt(endProgress).z * controlScale,
    });
    return {
      end,
      control1: {
        x: start.x + startTangent.x,
        y: start.y + startTangent.y,
        z: start.z + startTangent.z,
      },
      control2: {
        x: end.x - endTangent.x,
        y: end.y - endTangent.y,
        z: end.z - endTangent.z,
      },
      windingStartDegrees: windingAt(startProgress),
      windingEndDegrees: windingAt(endProgress),
    };
  });
}

function smootherStep(progress: number) {
  return progress ** 3 * (progress * (progress * 6 - 15) + 10);
}

function smootherStepDerivative(progress: number) {
  return 30 * progress ** 2 * (progress - 1) ** 2;
}

function smoothCrest(progress: number) {
  return 64 * progress ** 3 * (1 - progress) ** 3;
}

function smoothCrestDerivative(progress: number) {
  return 192 * progress ** 2 * (1 - progress) ** 2 * (1 - 2 * progress);
}

function analyticLoopSpans(lateralExit: number): AuthoredPieceDescriptor['spans'] {
  const forwardLength = 96;
  const meanTangentSpeed = 448;
  const tangentSpeedWeight = forwardLength * 2;
  return authoredParametricChain(
    12,
    (progress) => {
      const angle = Math.PI * 2 * progress;
      return {
        x:
          (meanTangentSpeed * Math.sin(angle)) / (Math.PI * 2) +
          tangentSpeedWeight * (progress / 2 + Math.sin(angle * 2) / (Math.PI * 8)),
        y:
          (meanTangentSpeed * (1 - Math.cos(angle))) / (Math.PI * 2) +
          (tangentSpeedWeight * Math.sin(angle) ** 2) / (Math.PI * 4),
        z: lateralExit * smootherStep(progress),
      };
    },
    (progress) => {
      const angle = Math.PI * 2 * progress;
      const tangentSpeed = meanTangentSpeed + tangentSpeedWeight * Math.cos(angle);
      return {
        x: tangentSpeed * Math.cos(angle),
        y: tangentSpeed * Math.sin(angle),
        z: lateralExit * smootherStepDerivative(progress),
      };
    },
  );
}

function analyticCorkscrewSpans(sign: -1 | 1): AuthoredPieceDescriptor['spans'] {
  const length = 256;
  const radius = 48;
  const lateralExit = sign * 64;
  return authoredParametricChain(
    12,
    (progress) => {
      const revolution = Math.PI * 2 * smootherStep(progress);
      return {
        x: length * progress,
        y: radius * (1 - Math.cos(revolution)),
        z: lateralExit * smootherStep(progress) - sign * radius * Math.sin(revolution),
      };
    },
    (progress) => {
      const easingDerivative = smootherStepDerivative(progress);
      const revolution = Math.PI * 2 * smootherStep(progress);
      const revolutionDerivative = Math.PI * 2 * easingDerivative;
      return {
        x: length,
        y: radius * Math.sin(revolution) * revolutionDerivative,
        z:
          lateralExit * easingDerivative -
          sign * radius * Math.cos(revolution) * revolutionDerivative,
      };
    },
    (progress) => {
      const winding = sign * 360 * smootherStep(progress);
      return winding === 0 ? 0 : winding;
    },
  );
}

function exactLoopHalf(direction: 'up' | 'down'): AuthoredPieceDescriptor['spans'] {
  const source = analyticLoopSpans(0);
  if (direction === 'up') return source.slice(0, source.length / 2);

  const midpoint = source[source.length / 2 - 1]!.end;
  const normalized = (value: number) => (value === 0 ? 0 : value);
  const reframe = (point: { x: number; y: number; z: number }) => ({
    x: normalized(-(point.x - midpoint.x)),
    y: normalized(point.y - midpoint.y),
    z: normalized(-(point.z - midpoint.z)),
  });
  return source.slice(source.length / 2).map((span) => ({
    ...span,
    end: reframe(span.end),
    control1: reframe(span.control1),
    control2: reframe(span.control2),
  }));
}

function exactCorkscrewHalf(
  side: 'left' | 'right',
  direction: 'to_inverted' | 'to_upright',
): AuthoredPieceDescriptor['spans'] {
  const targetSign: -1 | 1 = side === 'left' ? -1 : 1;
  const sourceSign: -1 | 1 = direction === 'to_inverted' ? targetSign : targetSign === -1 ? 1 : -1;
  const source = analyticCorkscrewSpans(sourceSign).map((span) => ({
    ...span,
    end: { ...span.end },
    control1: { ...span.control1 },
    control2: { ...span.control2 },
  }));
  const splitIndex = source.length / 2;
  const midpoint = source[splitIndex - 1]!.end;

  // Only the new inverted connector is normalized. Removing the forward
  // component from the adjoining handles makes the exact half-way cut face
  // precisely 90 degrees to the untouched upright connector. The handle's
  // inherited lateral magnitude, every endpoint, and both upright handles
  // remain unchanged from the full corkscrew.
  source[splitIndex - 1] = {
    ...source[splitIndex - 1]!,
    control2: {
      ...source[splitIndex - 1]!.control2,
      x: midpoint.x,
    },
  };
  source[splitIndex] = {
    ...source[splitIndex]!,
    control1: {
      ...source[splitIndex]!.control1,
      x: midpoint.x,
    },
  };

  if (direction === 'to_inverted') return source.slice(0, splitIndex);

  const normalized = (value: number) => (value === 0 ? 0 : value);
  const reframe = (point: { x: number; y: number; z: number }) => {
    const deltaX = point.x - midpoint.x;
    const deltaY = point.y - midpoint.y;
    const deltaZ = point.z - midpoint.z;
    return {
      x: normalized(sourceSign * deltaZ),
      y: normalized(deltaY),
      z: normalized(-sourceSign * deltaX),
    };
  };
  const midpointWinding = sourceSign * 180;
  return source.slice(splitIndex).map((span) => ({
    ...span,
    end: reframe(span.end),
    control1: reframe(span.control1),
    control2: reframe(span.control2),
    windingStartDegrees: normalized(
      (span.windingStartDegrees ?? midpointWinding) - midpointWinding,
    ),
    windingEndDegrees: normalized((span.windingEndDegrees ?? midpointWinding) - midpointWinding),
  }));
}

function helixDescriptor(side: 'left' | 'right', direction: 'up' | 'down') {
  const sign = side === 'left' ? -1 : 1;
  const riseSign = direction === 'up' ? 1 : -1;
  const radius = 64;
  const diagonal = 45;
  const anchors = [
    { x: 0, y: 0, z: 0 },
    { x: diagonal, y: riseSign * 4, z: sign * (radius - diagonal) },
    { x: radius, y: riseSign * 8, z: sign * radius },
    { x: diagonal, y: riseSign * 12, z: sign * (radius + diagonal) },
    { x: 0, y: riseSign * 16, z: sign * radius * 2 },
    { x: -diagonal, y: riseSign * 20, z: sign * (radius + diagonal) },
    { x: -radius, y: riseSign * 24, z: sign * radius },
    { x: -diagonal, y: riseSign * 28, z: sign * (radius - diagonal) },
    { x: 0, y: riseSign * 32, z: 0 },
  ] as const;
  return descriptor({
    id: `helix.${side}.${direction}`,
    label: `Helix ${side === 'left' ? 'Left' : 'Right'} · ${direction === 'up' ? 'Up' : 'Down'}`,
    category: 'turn',
    family: 'helix',
    description: `Banked 360° ${direction === 'up' ? 'climbing' : 'descending'} helix.`,
    pitches: LEVEL,
    banks: [side],
    spans: authoredChain(anchors, {
      entryTangent: { x: 16, y: 0, z: 0 },
      exitTangent: { x: 16, y: 0, z: 0 },
    }),
    mirrorId: `helix.${side === 'left' ? 'right' : 'left'}.${direction}`,
    tags: ['helix', 'banked', direction === 'down' ? 'descending' : 'climbing'],
  });
}

function pitchHandle(pitch: AuthoredPitch): { x: number; y: number } {
  switch (pitch) {
    case 90:
      return { x: 0, y: 10 };
    case 60:
      return { x: 5, y: 10 };
    case 25:
      return { x: 10, y: 5 };
    case 0:
      return { x: 10, y: 0 };
    case -25:
      return { x: 10, y: -5 };
    case -60:
      return { x: 5, y: -10 };
    case -90:
      return { x: 0, y: -10 };
  }
}

function pitchTransitionSpan(from: AuthoredPitch, to: AuthoredPitch, run: number, rise: number) {
  const entry = pitchHandle(from);
  const exit = pitchHandle(to);
  return [
    {
      end: { x: run, y: rise, z: 0 },
      control1: { x: entry.x, y: entry.y, z: 0 },
      control2: { x: run - exit.x, y: rise - exit.y, z: 0 },
    },
  ] as const;
}

function smoothSteepPitchTransition(
  from: 60 | 90 | -60 | -90,
  to: 60 | 90 | -60 | -90,
): AuthoredPieceDescriptor['spans'] {
  // The former 60/90 transition squeezed both tangent changes into a one-tile
  // rise. A two-level footprint gives the curve enough length to distribute
  // curvature while still landing on exact authored-grid coordinates.
  const end = { x: 16, y: Math.sign(from + to) * 64, z: 0 };
  const gridPitchVector = (pitch: 60 | 90 | -60 | -90) =>
    Math.abs(pitch) === 90 ? { x: 0, y: Math.sign(pitch) } : { x: 1, y: Math.sign(pitch) * 2 };
  const handleScale = 16;
  const entryVector = gridPitchVector(from);
  const exitVector = gridPitchVector(to);
  const control1 = {
    x: entryVector.x * handleScale,
    y: entryVector.y * handleScale,
    z: 0,
  };
  const control2 = {
    x: end.x - exitVector.x * handleScale,
    y: end.y - exitVector.y * handleScale,
    z: 0,
  };
  return [{ end, control1, control2 }];
}

function descriptor(options: {
  id: string;
  label: string;
  category: AuthoredPieceCategory;
  family?: string;
  description: string;
  pitches?: readonly AuthoredPitch[];
  banks?: readonly AuthoredBank[];
  orientations?: readonly AuthoredOrientation[];
  exitHeading?: number;
  exitPitch?: AuthoredPitch | 'preserve';
  exitBank?: AuthoredBank | 'preserve';
  exitOrientation?: AuthoredOrientation | 'preserve';
  winding?: number;
  spans: AuthoredPieceDescriptor['spans'];
  types?: readonly AuthoredTrackType[];
  mirrorId?: string;
  tags?: readonly string[];
  supportStyle?: AuthoredPieceDescriptor['supportStyle'];
}): AuthoredPieceDescriptor {
  return {
    id: options.id,
    label: options.label,
    category: options.category,
    family: options.family ?? options.category,
    description: options.description,
    entry: {
      pitches: options.pitches ?? ALL_PITCHES,
      banks: options.banks ?? ALL_BANKS,
      orientations: options.orientations ?? UPRIGHT,
    },
    exit: {
      headingDelta: options.exitHeading ?? 0,
      pitch: options.exitPitch ?? 'preserve',
      bank: options.exitBank ?? 'preserve',
      orientation: options.exitOrientation ?? 'preserve',
      windingQuarterTurnsDelta: options.winding ?? 0,
    },
    spans: options.spans,
    allowedTrackTypes: options.types ?? ['normal'],
    ...(options.mirrorId ? { mirrorId: options.mirrorId } : {}),
    tags: options.tags ?? [],
    supportStyle: options.supportStyle ?? 'standard',
  };
}

function straight(
  id: string,
  label: string,
  units: number,
  types: readonly AuthoredTrackType[] = ['normal', 'lift', 'launch'],
): AuthoredPieceDescriptor {
  return descriptor({
    id,
    label,
    category: 'straight',
    description: `${units / 32}-tile level straight that preserves banking.`,
    pitches: LEVEL,
    spans: straightSpan(units),
    types,
    tags: ['core'],
  });
}

function pitchTransition(
  from: AuthoredPitch,
  to: AuthoredPitch,
  run: number,
  rise: number,
): AuthoredPieceDescriptor {
  const direction = to > from ? 'up' : 'down';
  const id = `slope.${from < 0 ? `n${Math.abs(from)}` : from}.${to < 0 ? `n${Math.abs(to)}` : to}`;
  return descriptor({
    id,
    label: `${from}° → ${to}°`,
    category: 'slope',
    description: `Authored ${direction} transition from ${from}° to ${to}°.`,
    pitches: [from],
    exitPitch: to,
    spans:
      Math.abs(from) + Math.abs(to) === 150
        ? smoothSteepPitchTransition(from as 60 | 90 | -60 | -90, to as 60 | 90 | -60 | -90)
        : pitchTransitionSpan(from, to, run, rise),
    types:
      from >= 0 && to >= 0
        ? ['normal', 'lift', 'launch']
        : from > 0 && to >= 0
          ? ['normal', 'lift']
          : ['normal'],
    tags: ['slope', direction],
  });
}

function curve(
  id: string,
  label: string,
  headingDelta: -2 | -1 | 1 | 2,
  radius: 32 | 64 | 96,
  bank: AuthoredBank | null = null,
): AuthoredPieceDescriptor {
  const left = headingDelta < 0;
  const quarter = Math.abs(headingDelta) === 2;
  const lateral = (left ? -1 : 1) * (quarter ? radius : Math.round(radius / 2));
  const forward = radius;
  // Scale the authored handles with the selected radius. A fixed short handle
  // made "large" turns kink sharply at their ends and generated force spikes.
  // 0.5523r is the standard cubic approximation for a circular quarter arc.
  const curveFactor = Math.round(radius * (quarter ? 0.55228475 : 0.4));
  return descriptor({
    id,
    label,
    category: 'turn',
    description: `${quarter ? '90°' : '45°'} ${left ? 'left' : 'right'} turn.`,
    pitches: LEVEL,
    banks: bank ? [bank] : ALL_BANKS,
    exitHeading: headingDelta,
    spans: [
      {
        end: { x: forward, y: 0, z: lateral },
        control1: { x: curveFactor, y: 0, z: 0 },
        control2: quarter
          ? { x: forward, y: 0, z: lateral - Math.sign(lateral) * curveFactor }
          : {
              x: forward - curveFactor,
              y: 0,
              z: lateral - Math.sign(lateral) * curveFactor,
            },
      },
    ],
    mirrorId: id.includes('.left') ? id.replace('.left', '.right') : id.replace('.right', '.left'),
    tags: ['turn', quarter ? '90-degree' : '45-degree'],
  });
}

function bankTransition(
  from: AuthoredBank,
  to: AuthoredBank,
  length: 32 | 64,
): AuthoredPieceDescriptor {
  const bankName = (value: AuthoredBank) =>
    value === 'level' ? 'Level' : `45° ${value === 'left' ? 'Left' : 'Right'}`;
  return descriptor({
    id: `bank.${from}.${to}.${length / 32}t`,
    label: `${bankName(from)} → ${bankName(to)} · ${length / 32} ${
      length === 32 ? 'tile' : 'tiles'
    }`,
    category: 'bank',
    description: `Smooth ${length / 32}-tile banking transition.`,
    pitches: LEVEL,
    banks: [from],
    exitBank: to,
    spans: straightSpan(length),
    tags: ['bank-transition'],
  });
}

const descriptors: AuthoredPieceDescriptor[] = [
  descriptor({
    id: 'station.2t',
    label: 'Station',
    category: 'station',
    description: 'Two-tile station sized for the standard five-car train.',
    pitches: LEVEL,
    banks: LEVEL_BANK,
    spans: straightSpan(64),
    types: ['station'],
    tags: ['core'],
    supportStyle: 'station',
  }),
  straight('straight.1t', 'Straight · 1 tile', 32, ['normal', 'lift', 'launch', 'brake', 'photo']),
  straight('straight.2t', 'Straight · 2 tiles', 64, ['normal', 'lift', 'launch', 'brake', 'photo']),
  straight('straight.3t', 'Straight · 3 tiles', 96, ['normal', 'lift', 'launch']),
  pitchTransition(0, 25, 32, 8),
  pitchTransition(25, 60, 32, 24),
  // The vertical transition occupies a compact half-tile run and one level of
  // rise. The prior 32×40 footprint exaggerated the arc and read as a large
  // hump between otherwise modular 60° and 90° sections.
  pitchTransition(60, 90, 16, 32),
  pitchTransition(90, 60, 16, 32),
  pitchTransition(60, 25, 32, 24),
  pitchTransition(25, 0, 32, 8),
  pitchTransition(0, -25, 32, -8),
  pitchTransition(-25, -60, 32, -24),
  pitchTransition(-60, -90, 16, -32),
  pitchTransition(-90, -60, 16, -32),
  pitchTransition(-60, -25, 32, -24),
  pitchTransition(-25, 0, 32, -8),
  descriptor({
    id: 'slope.25.hold.1t',
    label: '25° Slope',
    category: 'slope',
    description: 'One-tile section that preserves a 25° slope.',
    pitches: [25],
    spans: tangentLinearSpan(32, 16, 10, 5),
    types: ['normal', 'lift', 'launch'],
    tags: ['slope', 'hold'],
  }),
  descriptor({
    id: 'slope.n25.hold.1t',
    label: '25° Down',
    category: 'slope',
    description: 'One-tile section that preserves a 25° descent.',
    pitches: [-25],
    spans: tangentLinearSpan(32, -16, 10, -5),
    tags: ['slope', 'hold'],
  }),
  descriptor({
    id: 'slope.60.hold.1t',
    label: '60° Slope',
    category: 'slope',
    description: 'Compact steep climbing section.',
    pitches: [60],
    spans: tangentLinearSpan(16, 32, 5, 10),
    types: ['normal', 'lift'],
    tags: ['slope', 'steep', 'hold'],
  }),
  descriptor({
    id: 'slope.n60.hold.1t',
    label: '60° Down',
    category: 'slope',
    description: 'Compact steep descending section.',
    pitches: [-60],
    spans: tangentLinearSpan(16, -32, 5, -10),
    tags: ['slope', 'steep', 'hold'],
  }),
  descriptor({
    id: 'slope.90.hold.1t',
    label: 'Vertical Up',
    category: 'slope',
    description: 'One-tile vertical ascent.',
    pitches: [90],
    spans: straightSpan(0, 32),
    types: ['normal', 'lift'],
    tags: ['slope', 'vertical', 'hold'],
  }),
  descriptor({
    id: 'slope.n90.hold.1t',
    label: 'Vertical Down',
    category: 'slope',
    description: 'One-tile vertical descent.',
    pitches: [-90],
    spans: straightSpan(0, -32),
    tags: ['slope', 'vertical', 'hold'],
  }),
  curve('turn.left.45.small', 'Left 45° · Small', -1, 32),
  curve('turn.right.45.small', 'Right 45° · Small', 1, 32),
  curve('turn.left.90.small', 'Left 90° · Small', -2, 32),
  curve('turn.right.90.small', 'Right 90° · Small', 2, 32),
  curve('turn.left.45.medium', 'Left 45° · Medium', -1, 64),
  curve('turn.right.45.medium', 'Right 45° · Medium', 1, 64),
  curve('turn.left.90.medium', 'Left 90° · Medium', -2, 64),
  curve('turn.right.90.medium', 'Right 90° · Medium', 2, 64),
  curve('turn.left.45.large', 'Left 45° · Large', -1, 96),
  curve('turn.right.45.large', 'Right 45° · Large', 1, 96),
  curve('turn.left.90.large', 'Left 90° · Large', -2, 96),
  curve('turn.right.90.large', 'Right 90° · Large', 2, 96),
  descriptor({
    id: 's_bend.left',
    label: 'S-Bend Left',
    category: 'turn',
    description: 'Two-tile lateral shift to the rider’s left.',
    pitches: LEVEL,
    spans: [
      {
        end: { x: 32, y: 0, z: -16 },
        control1: { x: 10, y: 0, z: 0 },
        control2: { x: 22, y: 0, z: -16 },
      },
      {
        end: { x: 64, y: 0, z: -32 },
        control1: { x: 42, y: 0, z: -16 },
        control2: { x: 54, y: 0, z: -32 },
      },
    ],
    mirrorId: 's_bend.right',
    tags: ['turn', 's-bend'],
  }),
  descriptor({
    id: 's_bend.right',
    label: 'S-Bend Right',
    category: 'turn',
    description: 'Two-tile lateral shift to the rider’s right.',
    pitches: LEVEL,
    spans: [
      {
        end: { x: 32, y: 0, z: 16 },
        control1: { x: 10, y: 0, z: 0 },
        control2: { x: 22, y: 0, z: 16 },
      },
      {
        end: { x: 64, y: 0, z: 32 },
        control1: { x: 42, y: 0, z: 16 },
        control2: { x: 54, y: 0, z: 32 },
      },
    ],
    mirrorId: 's_bend.left',
    tags: ['turn', 's-bend'],
  }),
  bankTransition('level', 'left', 32),
  bankTransition('left', 'level', 32),
  bankTransition('level', 'right', 32),
  bankTransition('right', 'level', 32),
  bankTransition('level', 'left', 64),
  bankTransition('left', 'level', 64),
  bankTransition('level', 'right', 64),
  bankTransition('right', 'level', 64),
  descriptor({
    id: 'helix.left.up',
    label: 'Helix Left · Up',
    category: 'turn',
    family: 'helix',
    description: 'Banked 360° climbing helix.',
    pitches: LEVEL,
    banks: ['left'],
    spans: [
      {
        end: { x: 32, y: 8, z: -32 },
        control1: { x: 16, y: 0, z: 0 },
        control2: { x: 32, y: 6, z: -16 },
      },
      {
        end: { x: 0, y: 16, z: -64 },
        control1: { x: 32, y: 10, z: -48 },
        control2: { x: 16, y: 14, z: -64 },
      },
      {
        end: { x: -32, y: 24, z: -32 },
        control1: { x: -16, y: 18, z: -64 },
        control2: { x: -32, y: 22, z: -48 },
      },
      {
        end: { x: 0, y: 32, z: 0 },
        control1: { x: -32, y: 26, z: -16 },
        control2: { x: -16, y: 32, z: 0 },
      },
    ],
    mirrorId: 'helix.right.up',
    tags: ['helix', 'banked'],
  }),
  descriptor({
    id: 'helix.right.up',
    label: 'Helix Right · Up',
    category: 'turn',
    family: 'helix',
    description: 'Banked 360° climbing helix.',
    pitches: LEVEL,
    banks: ['right'],
    spans: [
      {
        end: { x: 32, y: 8, z: 32 },
        control1: { x: 16, y: 0, z: 0 },
        control2: { x: 32, y: 6, z: 16 },
      },
      {
        end: { x: 0, y: 16, z: 64 },
        control1: { x: 32, y: 10, z: 48 },
        control2: { x: 16, y: 14, z: 64 },
      },
      {
        end: { x: -32, y: 24, z: 32 },
        control1: { x: -16, y: 18, z: 64 },
        control2: { x: -32, y: 22, z: 48 },
      },
      {
        end: { x: 0, y: 32, z: 0 },
        control1: { x: -32, y: 26, z: 16 },
        control2: { x: -16, y: 32, z: 0 },
      },
    ],
    mirrorId: 'helix.left.up',
    tags: ['helix', 'banked'],
  }),
];

function inversionDescriptors(): AuthoredPieceDescriptor[] {
  const loop = (side: 'left' | 'right') => {
    const lateral = side === 'left' ? -32 : 32;
    return descriptor({
      id: `loop.${side}`,
      label: `Vertical Loop ${side === 'left' ? 'Left' : 'Right'}`,
      category: 'inversion',
      family: 'vertical_loop',
      description: 'Four-tile teardrop loop with a distinct return lane.',
      pitches: LEVEL,
      banks: LEVEL_BANK,
      spans: [
        {
          end: { x: 24, y: 48, z: Math.round(lateral / 3) },
          control1: { x: 12, y: 0, z: 0 },
          control2: { x: 24, y: 32, z: lateral / 4 },
          windingEndDegrees: 90,
        },
        {
          end: { x: 48, y: 80, z: lateral },
          control1: {
            x: 24,
            y: 64,
            z: Math.round(lateral / 3) + (Math.round(lateral / 3) - lateral / 4),
          },
          control2: { x: 36, y: 80, z: lateral },
          windingStartDegrees: 90,
          windingEndDegrees: 180,
        },
        {
          end: { x: 72, y: 48, z: lateral },
          control1: { x: 60, y: 80, z: lateral },
          control2: { x: 72, y: 66, z: lateral },
          windingStartDegrees: 180,
          windingEndDegrees: 270,
        },
        {
          end: { x: 96, y: 0, z: lateral },
          control1: { x: 72, y: 32, z: lateral },
          control2: { x: 84, y: 0, z: lateral },
          windingStartDegrees: 270,
          windingEndDegrees: 360,
        },
      ],
      winding: 4,
      mirrorId: `loop.${side === 'left' ? 'right' : 'left'}`,
      tags: ['inversion', 'loop'],
      supportStyle: 'sparse_inversion',
    });
  };
  const roll = (side: 'left' | 'right') => {
    const sign = side === 'left' ? -1 : 1;
    return descriptor({
      id: `roll.${side}`,
      label: `Barrel Roll ${side === 'left' ? 'Left' : 'Right'}`,
      category: 'inversion',
      family: 'barrel_roll',
      description: 'Three-tile full roll with level entry and exit.',
      pitches: LEVEL,
      banks: LEVEL_BANK,
      spans: [
        {
          end: { x: 32, y: 8, z: sign * 8 },
          control1: { x: 10, y: 0, z: 0 },
          control2: { x: 22, y: 8, z: sign * 8 },
          windingEndDegrees: sign * 120,
        },
        {
          end: { x: 64, y: 8, z: -sign * 8 },
          control1: { x: 42, y: 8, z: sign * 8 },
          control2: { x: 54, y: 8, z: -sign * 8 },
          windingStartDegrees: sign * 120,
          windingEndDegrees: sign * 240,
        },
        {
          end: { x: 96, y: 0, z: 0 },
          control1: { x: 74, y: 8, z: -sign * 8 },
          control2: { x: 86, y: 0, z: 0 },
          windingStartDegrees: sign * 240,
          windingEndDegrees: sign * 360,
        },
      ],
      winding: sign * 4,
      mirrorId: `roll.${side === 'left' ? 'right' : 'left'}`,
      tags: ['inversion', 'roll'],
      supportStyle: 'sparse_inversion',
    });
  };
  const corkscrew = (side: 'left' | 'right') => {
    const sign = side === 'left' ? -1 : 1;
    return descriptor({
      id: `corkscrew.${side}`,
      label: `Corkscrew ${side === 'left' ? 'Left' : 'Right'}`,
      category: 'inversion',
      family: 'corkscrew',
      description: 'Compact lateral corkscrew with a one-tile lane offset.',
      pitches: LEVEL,
      banks: LEVEL_BANK,
      spans: [
        {
          end: { x: 32, y: 32, z: sign * 16 },
          control1: { x: 8, y: 0, z: 0 },
          control2: { x: 24, y: 28, z: sign * 10 },
          windingEndDegrees: sign * 180,
        },
        {
          end: { x: 64, y: 0, z: sign * 32 },
          control1: { x: 40, y: 36, z: sign * 22 },
          control2: { x: 56, y: 0, z: sign * 32 },
          windingStartDegrees: sign * 180,
          windingEndDegrees: sign * 360,
        },
      ],
      winding: sign * 4,
      mirrorId: `corkscrew.${side === 'left' ? 'right' : 'left'}`,
      tags: ['inversion', 'corkscrew'],
      supportStyle: 'sparse_inversion',
    });
  };
  const halfLoop = (direction: 'up' | 'down') =>
    descriptor({
      id: `half_loop.${direction}`,
      label: `Half Loop ${direction === 'up' ? 'Up' : 'Down'}`,
      category: 'inversion',
      family: 'half_loop',
      description: 'Half vertical loop between upright and inverted track.',
      pitches: LEVEL,
      banks: LEVEL_BANK,
      orientations: direction === 'up' ? ['upright'] : ['inverted'],
      exitOrientation: direction === 'up' ? 'inverted' : 'upright',
      spans:
        direction === 'up'
          ? [
              {
                end: { x: 24, y: 48, z: 0 },
                control1: { x: 8, y: 0, z: 0 },
                control2: { x: 24, y: 32, z: 0 },
                windingEndDegrees: 90,
              },
              {
                end: { x: 48, y: 64, z: 0 },
                control1: { x: 24, y: 60, z: 0 },
                control2: { x: 38, y: 64, z: 0 },
                windingStartDegrees: 90,
                windingEndDegrees: 180,
              },
            ]
          : [
              {
                end: { x: 24, y: -16, z: 0 },
                control1: { x: 8, y: 0, z: 0 },
                control2: { x: 24, y: -4, z: 0 },
                windingStartDegrees: 180,
                windingEndDegrees: 270,
              },
              {
                end: { x: 48, y: -64, z: 0 },
                control1: { x: 24, y: -32, z: 0 },
                control2: { x: 40, y: -64, z: 0 },
                windingStartDegrees: 270,
                windingEndDegrees: 360,
              },
            ],
      winding: 2,
      mirrorId: `half_loop.${direction === 'up' ? 'down' : 'up'}`,
      tags: ['inversion', 'half-loop'],
      supportStyle: 'sparse_inversion',
    });
  const halfRoll = (side: 'left' | 'right', direction: 'to_inverted' | 'to_upright') => {
    const sign = side === 'left' ? -1 : 1;
    const startingWinding = direction === 'to_inverted' ? 0 : sign * 180;
    const endingWinding = direction === 'to_inverted' ? sign * 180 : sign * 360;
    return descriptor({
      id: `half_roll.${side}.${direction}`,
      label: `${side === 'left' ? 'Left' : 'Right'} Half Roll ${
        direction === 'to_inverted' ? 'Up' : 'Down'
      }`,
      category: 'inversion',
      family: 'half_roll',
      description: 'Two-tile authored half roll between upright and inverted track.',
      pitches: LEVEL,
      banks: LEVEL_BANK,
      orientations: direction === 'to_inverted' ? ['upright'] : ['inverted'],
      exitOrientation: direction === 'to_inverted' ? 'inverted' : 'upright',
      spans: [
        {
          end: { x: 32, y: 8, z: sign * 8 },
          control1: { x: 10, y: 0, z: 0 },
          control2: { x: 22, y: 8, z: sign * 8 },
          windingStartDegrees: startingWinding,
          windingEndDegrees: startingWinding + sign * 90,
        },
        {
          end: { x: 64, y: 0, z: 0 },
          control1: { x: 42, y: 8, z: sign * 8 },
          control2: { x: 54, y: 0, z: 0 },
          windingStartDegrees: startingWinding + sign * 90,
          windingEndDegrees: endingWinding,
        },
      ],
      winding: sign * 2,
      mirrorId: `half_roll.${side === 'left' ? 'right' : 'left'}.${direction}`,
      tags: ['inversion', 'half-roll'],
      supportStyle: 'sparse_inversion',
    });
  };
  const halfCorkscrew = (side: 'left' | 'right', direction: 'to_inverted' | 'to_upright') => {
    const sign: -1 | 1 = side === 'left' ? -1 : 1;
    const forwardExit = 96;
    const verticalRadius = 48;
    const lateralExit = 96;
    const lateralSwingRadius = 96;
    const ascendingSpans = (travelSign: -1 | 1) =>
      authoredParametricChain(
        12,
        (progress) => {
          const horizontalTurn = (Math.PI / 2) * progress;
          const halfRevolution = Math.PI * progress;
          // A half corkscrew is one continuous half-revolution. Keep the roll
          // active across the entire authored piece instead of finishing it
          // early and leaving what reads as a straight tail before the exit.
          const circularSetup = Math.sin(halfRevolution) * progress;
          return {
            x: forwardExit * Math.sin(horizontalTurn),
            y: verticalRadius * (1 - Math.cos(halfRevolution)),
            z:
              travelSign *
              (lateralExit * (1 - Math.cos(horizontalTurn)) - lateralSwingRadius * circularSetup),
          };
        },
        (progress) => {
          const horizontalTurn = (Math.PI / 2) * progress;
          const halfRevolution = Math.PI * progress;
          const circularSetupDerivative =
            Math.PI * Math.cos(halfRevolution) * progress + Math.sin(halfRevolution);
          return {
            x: forwardExit * (Math.PI / 2) * Math.cos(horizontalTurn),
            y: verticalRadius * Math.PI * Math.sin(halfRevolution),
            z:
              travelSign *
              (lateralExit * (Math.PI / 2) * Math.sin(horizontalTurn) -
                lateralSwingRadius * circularSetupDerivative),
          };
        },
        (progress) => travelSign * 180 * progress,
      );
    const descendingSpans = () => {
      // The descending half is not a separately hand-authored curve. It is the
      // validated opposite-side rising half traversed backward, translated to
      // the origin, and rotated so its inverted entry faces local +X. This is
      // the exact geometric complement needed to finish a corkscrew while
      // preserving the named 90-degree exit.
      const sourceSign: -1 | 1 = sign === -1 ? 1 : -1;
      const source = ascendingSpans(sourceSign);
      const sourceEnd = source.at(-1)!.end;
      const rollSign = -sign;
      const reframe = (point: { x: number; y: number; z: number }) => {
        const x = point.x - sourceEnd.x;
        const y = point.y - sourceEnd.y;
        const z = point.z - sourceEnd.z;
        return {
          x: sign * z,
          y,
          z: -sign * x,
        };
      };
      return [...source].reverse().map((span, index) => {
        const sourceIndex = source.length - 1 - index;
        const sourceStart = sourceIndex === 0 ? { x: 0, y: 0, z: 0 } : source[sourceIndex - 1]!.end;
        return {
          end: reframe(sourceStart),
          control1: reframe(span.control2),
          control2: reframe(span.control1),
          windingStartDegrees: (rollSign * 180 * index) / source.length,
          windingEndDegrees: (rollSign * 180 * (index + 1)) / source.length,
        };
      });
    };
    return descriptor({
      id: `half_corkscrew.${side}.${direction}`,
      label: `${side === 'left' ? 'Left' : 'Right'} Half Corkscrew · ${
        direction === 'to_inverted' ? 'To Inverted' : 'To Upright'
      }`,
      category: 'inversion',
      family: 'half_corkscrew',
      description:
        direction === 'to_inverted'
          ? `Broad rising half corkscrew that exits inverted and turns 90° ${side}.`
          : `Broad descending half corkscrew that exits upright and turns 90° ${side}.`,
      pitches: LEVEL,
      banks: LEVEL_BANK,
      orientations: direction === 'to_inverted' ? ['upright'] : ['inverted'],
      exitHeading: sign * 2,
      exitPitch: 0,
      exitBank: 'level',
      exitOrientation: direction === 'to_inverted' ? 'inverted' : 'upright',
      winding: direction === 'to_inverted' ? sign * 2 : -sign * 2,
      spans: direction === 'to_inverted' ? ascendingSpans(sign) : descendingSpans(),
      mirrorId: `half_corkscrew.${side === 'left' ? 'right' : 'left'}.${direction}`,
      tags: ['inversion', 'half-corkscrew', direction],
      supportStyle: 'sparse_inversion',
    });
  };
  const zeroG = (side: 'left' | 'right') => {
    const sign = side === 'left' ? -1 : 1;
    return descriptor({
      id: `zero_g_roll.${side}`,
      label: `Zero-G Roll ${side === 'left' ? 'Left' : 'Right'}`,
      category: 'inversion',
      family: 'zero_g_roll',
      description: 'Four-tile rising and falling roll with level entry and exit.',
      pitches: LEVEL,
      banks: LEVEL_BANK,
      spans: [
        {
          end: { x: 32, y: 20, z: sign * 8 },
          control1: { x: 10, y: 0, z: 0 },
          control2: { x: 22, y: 18, z: sign * 8 },
          windingEndDegrees: sign * 90,
        },
        {
          end: { x: 64, y: 32, z: 0 },
          control1: { x: 42, y: 22, z: sign * 8 },
          control2: { x: 54, y: 32, z: 0 },
          windingStartDegrees: sign * 90,
          windingEndDegrees: sign * 180,
        },
        {
          end: { x: 96, y: 20, z: -sign * 8 },
          control1: { x: 74, y: 32, z: 0 },
          control2: { x: 86, y: 22, z: -sign * 8 },
          windingStartDegrees: sign * 180,
          windingEndDegrees: sign * 270,
        },
        {
          end: { x: 128, y: 0, z: 0 },
          control1: { x: 106, y: 18, z: -sign * 8 },
          control2: { x: 118, y: 0, z: 0 },
          windingStartDegrees: sign * 270,
          windingEndDegrees: sign * 360,
        },
      ],
      winding: sign * 4,
      mirrorId: `zero_g_roll.${side === 'left' ? 'right' : 'left'}`,
      tags: ['inversion', 'zero-g-roll'],
      supportStyle: 'sparse_inversion',
    });
  };
  return [
    loop('left'),
    loop('right'),
    roll('left'),
    roll('right'),
    corkscrew('left'),
    corkscrew('right'),
    halfLoop('up'),
    halfLoop('down'),
    halfRoll('left', 'to_inverted'),
    halfRoll('right', 'to_inverted'),
    halfRoll('left', 'to_upright'),
    halfRoll('right', 'to_upright'),
    halfCorkscrew('left', 'to_inverted'),
    halfCorkscrew('right', 'to_inverted'),
    halfCorkscrew('left', 'to_upright'),
    halfCorkscrew('right', 'to_upright'),
    zeroG('left'),
    zeroG('right'),
  ];
}

descriptors.push(...inversionDescriptors());

for (const side of ['left', 'right'] as const) {
  const source = descriptors.find((entry) => entry.id === `helix.${side}.up`)!;
  descriptors.push({
    ...source,
    id: `helix.${side}.down`,
    label: `Helix ${side === 'left' ? 'Left' : 'Right'} · Down`,
    description: 'Banked 360° descending helix.',
    mirrorId: `helix.${side === 'left' ? 'right' : 'left'}.down`,
    spans: source.spans.map((span) => ({
      ...span,
      end: { ...span.end, y: -span.end.y },
      control1: { ...span.control1, y: -span.control1.y },
      control2: { ...span.control2, y: -span.control2.y },
    })),
    tags: ['helix', 'banked', 'descending'],
  });
}

function repairedSpecialDescriptor(source: AuthoredPieceDescriptor): AuthoredPieceDescriptor {
  const [family, side = '', variant = ''] = source.id.split('.');
  const sign = side === 'left' ? -1 : 1;

  if (family === 'helix')
    return helixDescriptor(
      side as 'left' | 'right',
      (variant || (source.id.endsWith('.down') ? 'down' : 'up')) as 'up' | 'down',
    );

  if (family === 'loop') {
    const lateralExit = sign * 32;
    // The quintic lane change has zero velocity and curvature at both
    // connectors, so the grid offset happens inside the inversion without
    // introducing a pre-loop jog or an exit kink.
    const spans = analyticLoopSpans(lateralExit);
    return {
      ...source,
      description:
        'Analytic teardrop loop whose lateral lane change occurs continuously during the inversion.',
      spans,
      exit: {
        ...source.exit,
        headingDelta: 0,
        pitch: 0,
        bank: 'level',
        orientation: 'upright',
        windingQuarterTurnsDelta: 0,
      },
    };
  }

  if (family === 'roll' || family === 'zero_g_roll') {
    const zeroG = family === 'zero_g_roll';
    const length = zeroG ? 320 : 256;
    const crestHeight = zeroG ? 48 : 0;
    const spans = authoredParametricChain(
      12,
      (progress) => ({
        x: length * progress,
        // The zero-G variant follows a long, zero-curvature crest while the
        // barrel roll stays on a straight centerline. Neither snakes sideways.
        y: crestHeight * smoothCrest(progress),
        z: 0,
      }),
      (progress) => ({
        x: length,
        y: crestHeight * smoothCrestDerivative(progress),
        z: 0,
      }),
      (progress) => sign * 360 * smootherStep(progress),
    );
    return {
      ...source,
      description: zeroG
        ? 'Long zero-G crest with a smoothly eased full roll and level connectors.'
        : 'Long straight barrel roll with smoothly eased rotation and level connectors.',
      spans,
      exit: {
        ...source.exit,
        headingDelta: 0,
        pitch: 0,
        bank: 'level',
        orientation: 'upright',
        windingQuarterTurnsDelta: sign * 4,
      },
    };
  }

  if (family === 'corkscrew') {
    // The corkscrew advances continuously while the centerline completes one
    // circular revolution around the travel axis. The phase starts opposite
    // the named exit side and resolves into a two-cell lateral lane change.
    const spans = analyticCorkscrewSpans(sign as -1 | 1);
    return {
      ...source,
      description:
        'Full-size circular corkscrew with an eased single revolution and two-cell lateral exit.',
      spans,
      exit: {
        ...source.exit,
        headingDelta: 0,
        pitch: 0,
        bank: 'level',
        orientation: 'upright',
        windingQuarterTurnsDelta: sign * 4,
      },
    };
  }

  if (family === 's_bend') {
    const lateralExit = sign * 32;
    const length = 128;
    return {
      ...source,
      description:
        'Long grid-aligned lateral shift with zero heading and curvature at both connectors.',
      spans: authoredParametricChain(
        8,
        (progress) => ({
          x: length * progress,
          y: 0,
          z: lateralExit * smootherStep(progress),
        }),
        (progress) => ({
          x: length,
          y: 0,
          z: lateralExit * smootherStepDerivative(progress),
        }),
      ),
      exit: {
        ...source.exit,
        headingDelta: 0,
        pitch: 0,
        bank: 'preserve',
        orientation: 'preserve',
        windingQuarterTurnsDelta: 0,
      },
    };
  }

  if (family === 'half_loop') {
    const up = side === 'up';
    return {
      ...source,
      description: up
        ? 'Exact first half of the planar teardrop loop, ending at its level inverted crown.'
        : 'Exact second half of the planar teardrop loop, reframed from its level inverted crown.',
      spans: exactLoopHalf(up ? 'up' : 'down'),
      exit: {
        ...source.exit,
        headingDelta: 4,
        pitch: 0,
        bank: 'level',
        orientation: up ? 'inverted' : 'upright',
        windingQuarterTurnsDelta: 0,
      },
    };
  }

  if (family === 'half_corkscrew') {
    const direction = variant as 'to_inverted' | 'to_upright';
    const targetSign: -1 | 1 = side === 'left' ? -1 : 1;
    return {
      ...source,
      description:
        direction === 'to_inverted'
          ? `Exact first half of the ${side} full corkscrew, ending flat and inverted at 90 degrees.`
          : `Exact second half of the opposite full corkscrew, beginning flat and inverted and resolving ${side} to its untouched upright exit.`,
      spans: exactCorkscrewHalf(side as 'left' | 'right', direction),
      exit: {
        ...source.exit,
        headingDelta: targetSign * 2,
        pitch: 0,
        bank: 'level',
        orientation: direction === 'to_inverted' ? 'inverted' : 'upright',
        windingQuarterTurnsDelta: direction === 'to_inverted' ? targetSign * 2 : -targetSign * 2,
      },
    };
  }

  if (family === 'half_roll') {
    const toInverted = variant === 'to_inverted';
    const anchors = Array.from({ length: 5 }, (_, index) => {
      return {
        x: index * 16,
        y: 0,
        z: 0,
      };
    });
    const winding = anchors.map((_, index) => sign * 45 * index);
    return {
      ...source,
      spans: authoredChain(anchors, {
        windingDegrees: winding,
        entryTangent: { x: 10, y: 0, z: 0 },
        exitTangent: { x: 10, y: 0, z: 0 },
      }),
      exit: {
        ...source.exit,
        headingDelta: 0,
        pitch: 0,
        bank: 'level',
        orientation: toInverted ? 'inverted' : 'upright',
        windingQuarterTurnsDelta: sign * 2,
      },
    };
  }

  return source;
}

export const AUTHORED_TRACK_CATALOG: readonly AuthoredPieceDescriptor[] = Object.freeze(
  descriptors.map((entry) => Object.freeze(repairedSpecialDescriptor(entry))),
);

export const AUTHORED_TRACK_REGISTRY = new Map(
  AUTHORED_TRACK_CATALOG.map((entry) => [entry.id, entry] as const),
);

export function getAuthoredDescriptor(id: string): AuthoredPieceDescriptor | null {
  return AUTHORED_TRACK_REGISTRY.get(id) ?? null;
}

export function authoredCatalogByCategory(
  category: AuthoredPieceCategory,
): readonly AuthoredPieceDescriptor[] {
  return AUTHORED_TRACK_CATALOG.filter((entry) => entry.category === category);
}
